import { Link, Outlet } from 'react-router-dom';
import { Logo } from '@/components/shared/logo';
import { ROUTES } from '@/lib/constants';
import { ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';

/** Split-screen auth layout used by /login, /signup, /forgot-password, etc. */
export function AuthShell() {
  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <section className="relative flex flex-col p-6 sm:p-10">
        <Link to={ROUTES.home} aria-label="Tally home">
          <Logo />
        </Link>
        <div className="my-auto flex w-full justify-center">
          <div className="w-full max-w-md animate-in">
            <Outlet />
          </div>
        </div>
        <footer className="mt-6 text-xs text-(--color-muted-foreground)">
          By continuing you agree to Tally&rsquo;s{' '}
          <Link to="/terms" className="underline-offset-2 hover:underline">Terms</Link> and{' '}
          <Link to="/privacy" className="underline-offset-2 hover:underline">Privacy Policy</Link>.
        </footer>
      </section>

      <section className="hidden bg-gradient-to-br from-(--color-brand-from) via-(--color-brand-mid) to-(--color-brand-to) lg:flex">
        <div className="relative flex w-full items-center justify-center overflow-hidden p-12 text-white">
          <div className="grid-bg absolute inset-0 opacity-15" aria-hidden="true" />
          <div className="relative max-w-md space-y-8">
            <h2 className="text-3xl font-semibold leading-tight tracking-tight">
              Hire on evidence.<br />Get hired on evidence.
            </h2>
            <p className="text-white/80">
              Tally turns claims into receipts: every credential is verified, every change is
              audit-logged, and every recruiter sees only what the candidate chose to share.
            </p>
            <ul className="space-y-4">
              <Feature icon={ShieldCheck} text="Cryptographic credential verification" />
              <Feature icon={Sparkles} text="AI-assisted CV parsing in seconds" />
              <Feature icon={TrendingUp} text="Recruiter search built on facts, not keywords" />
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon: Icon, text }: { icon: typeof ShieldCheck; text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 grid h-7 w-7 place-items-center rounded-md bg-white/15 text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-white/90">{text}</span>
    </li>
  );
}
